import React, { FC, useState } from "react";
import { useSelector } from "react-redux";
import IssueInformationById from "./IssueInformationById";
import IssueQuickViewCard from "./IssueQuickViewCard";
import { CommentsTab } from "../../../components/comments/Comments";
import { TimeLogTable } from "../timeSheets/TimeLogTable";
import LinkIssueTable from "./LinkIssueTable";
import IssueResolutionTab from "./IssueResolutionTab";
import DocumentsTabWrapper from "../projectDocument/DocumentsTabWrapper";
import IssueStatusTimeLine from "./IssuesStatusTimeLine";
import IssueActivityStreamLine from "./IssueActivityTimeLine";
import { RootState } from "../../../store/store";

interface TabItem {
  label: string;
  tabKey: string;
  content: React.ReactNode;
}

const IssueInDetailView: FC<{
  id: number;
  activeTab: string;
  refreshData?: () => void;
  handleClose?: () => void;
}> = ({ id, activeTab, refreshData = () => {}, handleClose = () => {} }) => {
  const [tab, setTab] = useState(activeTab);
  const [viewMode, setViewMode] = useState<"summary" | "edit">("summary");

  const { data: IssueInDetailModal } = useSelector(
    (state: RootState) => state.IssueListInDetailModal
  );

  const { selectedIssueDetails } = IssueInDetailModal || {};

  const tabs: TabItem[] = [
    {
      label: "View",
      tabKey: "view",
      content: (
        <div>
          {viewMode === "summary" ? (
            <IssueQuickViewCard
              onEdit={() => setViewMode("edit")}
              onRefresh={refreshData}
            />
          ) : (
            <IssueInformationById
              refreshData={selectedIssueDetails}
              handleClose={handleClose}
              isFromWorkUI={false}
              hideInternalTabs={true}
              initialEditMode={true}
              onBackToSummary={() => setViewMode("summary")}
            />
          )}
        </div>
      ),
    },
    {
      label: "Comments",
      tabKey: "comments",
      content: (
        <div>
          <CommentsTab
            module="Issue"
            moduleId={selectedIssueDetails?.issueId || id}
            projectId={selectedIssueDetails?.projectId}
            projectName={selectedIssueDetails?.projectName}
            name={selectedIssueDetails?.issueName}
            showTaskModal={false}
            isFromWorkUI={false}
          />
        </div>
      ),
    },
    {
      label: "Log Hours",
      tabKey: "loghours",
      content: (
        <div>
          <TimeLogTable
            projectId={selectedIssueDetails?.projectId}
            issueId={selectedIssueDetails?.issueId || id}
            activeTab={tab}
            isFromWorkUI={false}
            moduleName="Issues"
            isFromSpecificLevel={true}
            issueOwnerId={selectedIssueDetails?.assignedTo}
            issueOwnerName={selectedIssueDetails?.IssueAssignedToName}
            onTaskCompletionUpdate={refreshData}
            refreshParentTask={refreshData}
          />
        </div>
      ),
    },
    {
      label: "Link Issue",
      tabKey: "LinkIssue",
      content: (
        <div>
          <LinkIssueTable
            projectId={selectedIssueDetails?.projectId}
            minimalMode={true}
            issueDetails={selectedIssueDetails}
            activeTab={tab}
            isFromWorkUI={false}
            onIssueLinked={refreshData}
          />
        </div>
      ),
    },
    {
      label: "Resolution",
      tabKey: "Resolution",
      content: (
        <div>
          <IssueResolutionTab
            activeTab={tab}
            issueDetails={selectedIssueDetails}
            isFromWorkUI={false}
          />
        </div>
      ),
    },
    {
      label: "Documents",
      tabKey: "Documents",
      content: (
        <div>
          <DocumentsTabWrapper
            linkType="issue"
            shouldLoadData={tab === "Documents"}
            isFromWorkUI={false}
          />
        </div>
      ),
    },
    {
      label: "Timeline",
      tabKey: "StatusTimeLine",
      content: (
        <div>
          <IssueStatusTimeLine activeTab={tab} />
        </div>
      ),
    },
    {
      label: "Activity",
      tabKey: "ActivityStream",
      content: (
        <div>
          <IssueActivityStreamLine activeTab={tab} />
        </div>
      ),
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {/* Tabs Header */}
      <div
        style={{
          borderBottom: "1px solid #f0f0f0",
          padding: "0 16px",
          background: "#fafafa",
          flexShrink: 0,
        }}
      >
        <ul
          className="nav nav-tabs nav-tabs-card nav-tabs-xs"
          style={{
            display: "flex",
            flexWrap: "nowrap",
            overflowX: "auto",
            overflowY: "hidden",
            margin: 0,
            padding: 0,
            listStyle: "none",
            gap: 0,
          }}
        >
          {tabs.map((tabItem) => (
            <li
              key={tabItem.tabKey}
              className="nav-item"
              style={{ flexShrink: 0 }}
            >
              <a
                href={`#${tabItem.tabKey}`}
                className={`nav-link${tab === tabItem.tabKey ? " active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  // Refetch the issue when returning to View so cross-tab
                  // edits (comments, log hours, resolution, etc.) are
                  // reflected without a manual reload.
                  if (
                    tabItem.tabKey === "view" &&
                    tab !== "view" &&
                    typeof refreshData === "function"
                  ) {
                    refreshData();
                  }
                  setTab(tabItem.tabKey);
                }}
                style={{
                  cursor: "pointer",
                  padding: "12px 16px",
                  display: "block",
                  color: tab === tabItem.tabKey ? "#1890ff" : "#666",
                  fontWeight: tab === tabItem.tabKey ? 500 : 400,
                  borderBottom: tab === tabItem.tabKey ? "2px solid #1890ff" : "2px solid transparent",
                  background: "transparent",
                  whiteSpace: "nowrap",
                }}
              >
                {tabItem.label}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Tab Content */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          padding: "16px",
        }}
      >
        {tabs.map((tabItem) => (
          <div
            key={tabItem.tabKey}
            style={{
              display: tab === tabItem.tabKey ? "block" : "none",
            }}
          >
            {tab === tabItem.tabKey && tabItem.content}
          </div>
        ))}
      </div>
    </div>
  );
};

export default IssueInDetailView;
